import type { ChartOptions } from 'chart.js'
import { format } from 'date-fns/format'
import memoize from 'lodash/memoize'

import {
  COLOR_BOUGHT,
  COLOR_LOCKED,
  chartFonts,
} from '@/modules/chatting/ui/chat-window/fan-info/ppv-stats/ppv-timeline/consts.ts'
import { getCustomTooltip } from '@/modules/chatting/ui/chat-window/fan-info/ppv-stats/ppv-timeline/tooltip.ts'
import type { TimelineData } from '@/modules/chatting/ui/chat-window/fan-info/ppv-stats/ppv-timeline/types'
import { toUsdFormat } from '@/modules/chatting/ui/chat-window/fan-info/utils.ts'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const memoResolver = (...args: any[]) => args.join('-')

function roundImage(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height - radius)
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  ctx.lineTo(x + radius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

const getRoundedImage = memoize(
  (preview: string, isMediaBlurEnabled?: boolean) => {
    const canvas = document.createElement('canvas'),
      ctx = canvas.getContext('2d'),
      radius = 6,
      height = 62,
      width = 72

    canvas.height = height
    canvas.width = width

    if (!ctx) {
      return canvas
    }

    roundImage(ctx, 0, 0, width, height, radius)
    ctx.clip()

    const img = new Image()

    img.onload = () => {
      if (isMediaBlurEnabled) {
        const blurSize = 15

        ctx.filter = 'blur(15px)'

        ctx.drawImage(
          img,
          -blurSize,
          -blurSize,
          width + blurSize * 2,
          height + blurSize * 2,
        )
      } else {
        ctx.drawImage(img, 0, 0, width, height)
      }
    }
    img.onerror = () => {
      ctx.fillStyle = '#e0e0e0'
      ctx.fillRect(0, 0, width, height)
    }

    img.src = preview

    return canvas
  },
  memoResolver,
)

export function getOptions(
  data: TimelineData[],
  tickColor: string,
  isMediaBlurEnabled?: boolean,
): ChartOptions<'line'> {
  const customTooltip = getCustomTooltip(data)

  return {
    maintainAspectRatio: false,
    elements: {
      line: { tension: 0.5 },
      point: {
        pointStyle: (context) =>
          data[context.dataIndex]
            ? getRoundedImage(
                data[context.dataIndex].preview,
                isMediaBlurEnabled,
              )
            : false,
        radius: 32,
        hoverRadius: 32,
      },
    },
    scales: {
      y: {
        border: {
          color: tickColor,
        },
        offset: true,
        beginAtZero: true,
        grid: {
          display: false,
          color: tickColor,
        },
        ticks: {
          font: chartFonts.bodyFont,
          callback: function (value) {
            return '$' + value
          },
        },
      },
      x: {
        border: {
          color: tickColor,
        },
        offset: true,
        grid: { color: tickColor },
        ticks: {
          padding: 8,
          font: chartFonts.bodyFont,
          callback: function (value) {
            const label = this.getLabelForValue(Number(value))

            return format(new Date(label), 'MMM, d')
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        anchor: 'start',
        align: 'start',
        offset: -23,

        borderRadius: 4,
        backgroundColor: function (context) {
          if (!data[context.dataIndex]) {
            return COLOR_LOCKED
          }
          const isSold = data[context.dataIndex].isOpened

          return isSold ? COLOR_BOUGHT : COLOR_LOCKED
        },
        padding: {
          right: 5,
          left: 5,
        },
        textAlign: 'center',
        color: 'white',
        font: {
          ...chartFonts.bodyFont,
          size: 10,
        },

        formatter: (value) => toUsdFormat(value?.y ?? 0, true),
      },

      tooltip: {
        enabled: false,
        external: customTooltip,
      },
    },
  }
}
